import * as _ from "lodash";
import {ASync} from "../PA.Core/Basic/ASync";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {
    IMessageProvider,
    IProgressProvider, MessageType
} from "../PA.Core/PA.Core.IF.Basic";
import {IBusinessObject, IBusinessObjectChanges, IBusinessObjectChangesOptions} from "../PA.Core/PA.Core.IF.BO";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {
    CreateOptions,
    ESRecordResolver
} from "../PA.Core/lib/ESRecordResolver"
import {PAdbContext} from "../PA.Core/PAdbContext";
import {esWB_Product_Type} from "../PA.Core/PAdbSchema";
import {WB_ProductTypeRow} from "../PA.Core/PAdbSchemaRows";
import { IwbProductType} from "../PA.Core/PAdbSchemaRowsIF";
import {PAEnvironment} from "../PA.Core/PAEnvironment";
import {RecordUtils} from "../PA.Core/Basic/Records";
import {ProductTypeBOValidations} from "./ProductTypeBO.Validations";
import {DefaultChanges} from "../PA.Core/BusinessObject/BusinessObjectChanges";
import {IEntityMetaData} from "../PA.MD.Core/PA.MD.IF.Entity";
import {EntityMetaDataManager} from "../PA.MD.Core/EntityMetaDataManager";

export class ProductTypeBO
    implements
        IBusinessObject
{
    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOption: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_ProductType: WB_ProductTypeRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};
        if ('dbContext' in this.CreateOptions) {
            this.dbContext = this.CreateOptions.dbContext;
        }

        this.Identity = new IdentityProvider({id: 0, name: 'New Product Type'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = null;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOption = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider('Validating', this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider('Saving', this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider('Deleting', this);

        this.drWB_ProductType = null;
    }

    //</editor-fold>

    //<editor-fold desc = "IBusinessObject operations">
    get WB_ProductType(): WB_ProductTypeRow {
        return this.drWB_ProductType;
    }

    async LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Product Type");
        let olc = self._OnLoadComplete = (async () => {
            self.LoadOptions = options || {};

            try {
                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for new Product Type.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                let newProductTypeRow: WB_ProductTypeRow = <WB_ProductTypeRow>await self.dbContext.createRecordWithKey(esWB_Product_Type)
                newProductTypeRow.setDefaults();

                self.drWB_ProductType = newProductTypeRow;
                self.dbContext.add(esWB_Product_Type, newProductTypeRow);
                this.WB_ProductType.cif.WB_MDSET_ID = options?.wbMasterDataSetId || 0
                this.WB_ProductType.cif.PRDM_ID = options?.sId || this.WB_ProductType.cif.PRDM_ID || ''
                this.WB_ProductType.cif.PRDM_NAME = options?.sName || this.WB_ProductType.cif.PRDM_NAME || ''
                this.WB_ProductType.cif.PRDM_TYPE = options?.sType || this.WB_ProductType.cif.PRDM_TYPE || ''
                self.updateIdentity();
                await self.IEntity_OnLoadComplete()

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                return undefined
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }

        })();
        return olc;
    }

    async Load(anId: number | string, options?: object): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        this.LoadProgressProvider.start('Loading (' + self.Identity.id_ui + ')');
        let olc = this._OnLoadComplete = (async () => {
            try {
                if (self.bIsLoaded) {
                    const sError = 'Product Type already loaded.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                self.LoadOptions = options || {};

                if ('dbContext' in self.LoadOptions) {
                    self.dbContext = self.LoadOptions.dbContext;
                } else if (self.dbContext == null) {
                    self.dbContext = new PAdbContext();
                }

                if (_.isEmpty(this.dbContext)) {
                    const sError = 'Unable to determine dbContext for Product Type.';
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return this;
                }

                if ('RecordResolver' in self.LoadOptions) {
                    recordResolver = (<any>self.LoadOptions).RecordResolver;
                    recordResolverLoadPromise = ASync.resolve();
                } else {
                    recordResolver = ProductTypeBO.createRecordResolver({dbContext: self.dbContext});
                    recordResolverLoadPromise = ProductTypeBO.setupRecordResolver(recordResolver, [anId]);
                }

                await recordResolverLoadPromise

                const aProductTypeRow: any = recordResolver.get(esWB_Product_Type, anId);

                if (_.isEmpty(aProductTypeRow)) {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception('Id not found.');
                    return self;
                }
                self.drWB_ProductType = aProductTypeRow;
                self.updateIdentity();
                await self.IEntity_OnLoadComplete()

                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                self.LoadProgressProvider.finish();
                return self
            } catch (err: any) {
                self._OnLoadComplete = undefined;
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })();

        return olc;
    }

    private updateIdentity(): void {
        const {
            WB_PRDM_ID,
            PRDM_ID,
            PRDM_NAME
        } = this.WB_ProductType.cif;
        this.Identity.setAll(WB_PRDM_ID, PRDM_ID, PRDM_NAME);
        this.LoadProgressProvider.setName(
            'Loading ' + this.Identity.name + '(' + this.Identity.id_ui + ')'
        );
        this.Identity.listenToRecordUpdates(this.WB_ProductType, 'WB_PRDM_ID', 'PRDM_ID', 'PRDM_NAME');

        let userContext = PAEnvironment.getValue(PAEnvironment.UserContext, 'UserContext')
        let bHasRole = userContext.hasRoleId("Product_Data_Editor");
        if (bHasRole)
            this.Identity.enableRecordWriteBack(this.WB_ProductType,'PRDM_ID', 'PRDM_NAME')
        else
            this.Identity.enableRecordWriteBack(this.WB_ProductType,undefined, undefined)

    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    async Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start(
            'Validating ' + self.WB_ProductType.cif.PRDM_NAME + '(' + self.WB_ProductType.cif.PRDM_ID + ')'
        )

            self.ValidateOption = options || {};
            try {
            if (self.dbContext == undefined) {
                const sError = 'Unable to determine dbContext to validate Product Type.';
                self.ValidateProgressProvider.exception(sError);
                throw sError;
            }

            await ProductTypeBOValidations.ValidateFields(self)

                if(self.EntityMetaData){
                    self.EntityMetaData.WB_EntityRow.cif.ENTITY_ID = self.WB_ProductType.cif.PRDM_ID
                    self.EntityMetaData.WB_EntityRow.cif.ENTITY_NAME = self.WB_ProductType.cif.PRDM_NAME
                    self.EntityMetaData.WB_EntityRow.cif.SET_TYPE = this.WB_ProductType.cif.PRDM_TYPE
                    self.EntityMetaData.WB_EntityRow.cif.WB_MDSET_ID = self.WB_ProductType.cif.WB_MDSET_ID
                    await self.EntityMetaData.Validate()
                    self.ValidateMessageProvider.addMessages(self.EntityMetaData.ValidateMessageProvider.Messages)
                }
            if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                    self._bIsValidated = false;
                } else {
                    self._bIsValidated = true;
                }
                self.ValidateProgressProvider.finish();
                return self

            } catch (err) {
                self._bIsValidated = false;
                self.ValidateProgressProvider.exception(err);

            }
        return self
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbProductType = <IwbProductType>self.drWB_ProductType;
        this.SaveProgressProvider.start(
            'Saving ' + wbProductType.PRDM_NAME + '(' + wbProductType.PRDM_ID + ')'
        );
        try {
            if (_.isEmpty(self.dbContext)) {
                self._bIsSaved = false;
                const sError = 'Unable to determine dbContext to Save Product Type.';
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>;

            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            if (!options?.bSkipValidations) {
                await self.Validate({})

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    return self
                }
            }
            await saveFunction(esWB_Product_Type, self.drWB_ProductType, {})
            if (self.EntityMetaData) {
                await self.EntityMetaData.Save(bBatch)
            }
            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }

            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);

        } finally {
            return self
        }

    }


    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbProductType = <IwbProductType>this.drWB_ProductType;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start(
            'Deleting ' + wbProductType.PRDM_NAME + '(' + wbProductType.PRDM_ID + ')'
        );

        return new Promise<IBusinessObject>((resolve, reject) => {
            self.drWB_ProductType.markDeleted();
            if(self.EntityMetaData)
                self.EntityMetaData.Delete(bSaveNow)
            if (bSaveNow) {
                self.SaveUsing(false)
                    .then(() => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);
                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    });
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    async Copy(options?: any): Promise<IBusinessObject> {
        const self = this

        const src: ProductTypeBO = this;

        const tgt = new ProductTypeBO({});
        await tgt.LoadNew()

        if (!tgt.bIsLoaded)
            throw new Error("Unable to create New Product Type")

        RecordUtils.CopyProperties(tgt.dbContext, esWB_Product_Type,
            src.WB_ProductType, tgt.WB_ProductType,
            [],
            ['DELETED_DT', 'DELETED_IND'],
            {})

        tgt.WB_ProductType.cif.PRDM_ID = "CopyOf" + src.WB_ProductType.cif.PRDM_ID
        tgt.WB_ProductType.cif.PRDM_NAME = "Copy of " + src.WB_ProductType.cif.PRDM_NAME
        tgt.Identity.setAll(tgt.WB_ProductType.cif.WB_PRDM_ID, tgt.WB_ProductType.cif.PRDM_ID, tgt.WB_ProductType.cif.PRDM_NAME);

        return tgt
    }

    Changes(options?: IBusinessObjectChangesOptions): IBusinessObjectChanges {
        let opts = options || {}
        opts.dctIncludeBelongsToNamedRelations = opts.dctIncludeBelongsToNamedRelations || {}
        opts.dctExcludeFields = opts.dctExcludeFields || {}

        // Set any BO Specific change settings
        return DefaultChanges(this, this.drWB_ProductType, opts)
    }
    //</editor-fold>

    //<editor-fold desc = "Entity">
    _entityMetaData: IEntityMetaData

    IEntity_init(): void {
        // Nothing can be created here
    }

    async IEntity_OnLoadComplete(): Promise<void> {
        const self = this

        let eMgr = new EntityMetaDataManager()
        let metaWbId = this.WB_ProductType.cif.WB_PRDM_ID
        let entityId = this.WB_ProductType.cif.PRDM_ID
        let entityName = this.WB_ProductType.cif.PRDM_NAME
        let wbMasterDataSetId = this.WB_ProductType.cif.WB_MDSET_ID

        let appExecId_default = this.WB_ProductType.cif.PRDM_TYPE
        let doId = "/AZR/WB_PRDGRP"
        let setType = ""
        switch (this.WB_ProductType.cif.PRDM_TYPE){
            case "Product":
                doId = "/AZR/WB_PROD"
                setType = "Product"
                break;
            case "ProductGroup":
                doId = "/AZR/WB_PRDGRP"
                setType = "ProductGroup"
                break;
        }


        let emd = await eMgr.findOrCreate({
            dbContext: this.dbContext,
            appExecId: [entityId, appExecId_default],
            dataObjectId: "/AZR/WB_CUST",
            dataObjectTypeId: doId,
            setType: setType,
            metaType: "WB_PrdM",
            metaWbId: metaWbId,
            wbMasterDataSetId: wbMasterDataSetId,
            entityId: entityId,
            entityName: entityName,
            dimensionId: "Product"
        })

        this._entityMetaData = emd

    }

    get EntityMetaData(): IEntityMetaData { return this._entityMetaData }


    //</editor-fold>

    //<editor-fold desc = "staticIRecordResolver">
    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        const opts = boLoadOptions || {};
        let dbContext: PAdbContext;
        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const product_type_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {}
        };

        return new ESRecordResolver(dbContext, product_type_options);
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: (string | number)[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver.findAllWithRelationships(esWB_Product_Type, ids)
                .then(() => {
                    resolve();
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass( ProductTypeBO , "ProductTypeBO", "Product Type")